import { Button } from 'primereact/button'
import { Divider } from 'primereact/divider'
import MercosulPlate from 'shared/mercosul-plate/mercosul-license-plate'
import { AlarmCriterion } from '../types/alarm-criterion'
import { AhcrCriterionTypeEnum, AnprCriterionTypeEnum, CriterionType } from '../types/alarm.types'
import { alarmColors } from '../types/alarm-consts'
import { useAlarm } from '../hooks/useAlarm'

interface AlarmCriteriaListProps {
  criteria: AlarmCriterion[]
  onRemoveCriterion: (id: string) => void
}

const AlarmCriteriaList: React.FC<AlarmCriteriaListProps> = ({
  criteria,
  onRemoveCriterion,
}) => {
  const { anprAlarmCriteria, ahcrAlarmCriteria } = useAlarm()

  const getCriterionTypeLabel = (type: CriterionType): string => {
    const allOptions = [...anprAlarmCriteria, ...ahcrAlarmCriteria]
    return allOptions.find((opt) => opt.key === type)?.displayValue || type
  }

  const getCriterionTypeIcon = (type: CriterionType): string => {
    const allOptions = [...anprAlarmCriteria, ...ahcrAlarmCriteria]
    return allOptions.find((opt) => opt.key === type)?.displayValue || type
  }

  const getCriterionBadgeStyle = (criterion: AlarmCriterion) => {
    if (criterion.category === 'ANPR') {
      return {
        background: 'linear-gradient(45deg, #3b82f6, #1d4ed8)',
        color: 'white',
      }
    } else {
      return {
        background: 'linear-gradient(45deg, #8b5cf6, #7c3aed)',
        color: 'white',
      }
    }
  }

  const renderCriterionValue = (criterion: AlarmCriterion) => {
    if (criterion.type === AnprCriterionTypeEnum.LICENSE_PLATE) {
      return (
        <div className='alarms-plate-cell'>
          <MercosulPlate
            licensePlate={criterion.value}
            size='S'
          />
        </div>
      )
    } else if (
      criterion.type === AnprCriterionTypeEnum.COLOR ||
      criterion.type === AhcrCriterionTypeEnum.JACKET_COLOR ||
      criterion.type === AhcrCriterionTypeEnum.TROUSER_COLOR
    ) {
      const colorObj = alarmColors.find(
        (c) => c.value === criterion.value
      )
      return (
        <div className='alarms-color-cell'>
          <div
            className='alarms-color-swatch'
            style={{
              backgroundColor: colorObj?.hex || '#000',
            }}
          />
          <span className='alarms-color-name'>{colorObj?.name}</span>
        </div>
      )
    } else {
      return (
        <span style={{ fontWeight: '600', color: '#f3f4f6' }}>
          {criterion.displayValue}
        </span>
      )
    }
  }

  return (
    <div className='alarms-form-right'>
      <div
        className='alarms-form-section alarms-criteria-section'
        style={{ height: '100%' }}
      >
        <h4 className='alarms-section-title'>
          <i className='pi pi-list' />
          Critérios Adicionados ({criteria.length})
        </h4>
        <Divider />

        {criteria.length > 0 ? (
          <div className='grid'>
            {criteria.map((criterion) => {
              const badgeStyle = getCriterionBadgeStyle(criterion)
              return (
                <div
                  key={criterion.id}
                  className='col-12 md:col-6'
                >
                  <div className='alarms-criterion-card'>
                    <div className='alarms-criterion-header'>
                      <div className='alarms-criterion-badges'>
                        <span
                          className='alarms-criterion-category-badge'
                          style={{
                            background: badgeStyle.background,
                            color: badgeStyle.color,
                          }}
                        >
                          <i
                            className={
                              criterion.category === 'ANPR'
                                ? 'pi pi-car'
                                : 'pi pi-users'
                            }
                          />
                          {criterion.category === 'ANPR'
                            ? 'Veículos'
                            : 'Pessoas'}
                        </span>
                        <span className='alarms-criterion-type-badge'>
                          <i className={getCriterionTypeIcon(criterion.type)} />
                          {getCriterionTypeLabel(criterion.type)}
                        </span>
                      </div>
                      <Button
                        icon='pi pi-trash'
                        rounded
                        text
                        severity='danger'
                        className='alarms-criterion-delete'
                        onClick={() => onRemoveCriterion(criterion.id)}
                        tooltip='Remover critério'
                        tooltipOptions={{ position: 'left' }}
                      />
                    </div>
                    <div className='alarms-criterion-value'>
                      {renderCriterionValue(criterion)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className='alarms-criteria-empty'>
            <i className='pi pi-inbox' />
            <p>Nenhum critério adicionado</p>
            <small>Adicione critérios usando o formulário ao lado</small>
          </div>
        )}
      </div>
    </div>
  )
}

export default AlarmCriteriaList
